/**
 * The invoice total, spelled out in English.
 *
 * The A4 tax invoice prints a line like "One Thousand Seven Hundred Fifty SR
 * Only" under the totals — a convention Gulf invoices inherit from cheques,
 * and one the tenant's customers (and their auditors) expect to find there.
 *
 * English only, on purpose: the reference sheet the ERP produces spells the
 * amount in English regardless of the invoice's other language.
 */

const ONES = [
  '', 'One', 'Two', 'Three', 'Four', 'Five', 'Six', 'Seven', 'Eight', 'Nine',
  'Ten', 'Eleven', 'Twelve', 'Thirteen', 'Fourteen', 'Fifteen', 'Sixteen',
  'Seventeen', 'Eighteen', 'Nineteen',
];

const TENS = [
  '', '', 'Twenty', 'Thirty', 'Forty', 'Fifty', 'Sixty', 'Seventy', 'Eighty', 'Ninety',
];

/** Scale words, ascending. Anything past a trillion prints as digits. */
const SCALES = ['', 'Thousand', 'Million', 'Billion', 'Trillion'];

/** 0–999 in words. Returns '' for 0 so callers can drop empty groups. */
function underThousand(n: number): string {
  const parts: string[] = [];
  const hundreds = Math.floor(n / 100);
  const rest = n % 100;
  if (hundreds > 0) parts.push(`${ONES[hundreds]} Hundred`);
  if (rest > 0 && rest < 20) {
    parts.push(ONES[rest] as string);
  } else if (rest >= 20) {
    const tens = Math.floor(rest / 10);
    const ones = rest % 10;
    parts.push(ones > 0 ? `${TENS[tens]} ${ONES[ones]}` : (TENS[tens] as string));
  }
  return parts.join(' ');
}

/** A non-negative integer in words ('Zero' for 0). */
export function integerInWords(value: number): string {
  let n = Math.floor(Math.abs(value));
  if (!Number.isFinite(n) || n === 0) return 'Zero';
  if (n >= 10 ** (SCALES.length * 3)) return String(n);

  const groups: string[] = [];
  let scale = 0;
  while (n > 0) {
    const group = n % 1000;
    if (group > 0) {
      const words = underThousand(group);
      groups.unshift(scale > 0 ? `${words} ${SCALES[scale]}` : words);
    }
    n = Math.floor(n / 1000);
    scale += 1;
  }
  return groups.join(' ');
}

/**
 * The full line: "One Thousand Seven Hundred Fifty SR Only".
 *
 * Fractions print as a vulgar fraction of the minor unit rather than more
 * words ("… and 25/100 SR Only") — shorter, unambiguous, and what the cheque
 * convention does with an odd number of halalas.
 */
export function amountInWords(amount: number, currency: string, precision = 2): string {
  const safe = Number.isFinite(amount) ? amount : 0;
  const negative = safe < 0;
  const abs = Math.abs(safe);
  const whole = Math.floor(abs);
  const factor = 10 ** precision;
  // Round the minor units, and carry when rounding lands on a whole unit.
  let minor = Math.round((abs - whole) * factor);
  let major = whole;
  if (minor >= factor) {
    major += 1;
    minor = 0;
  }

  const head = `${negative ? 'Minus ' : ''}${integerInWords(major)}`;
  const fraction = minor > 0 ? ` and ${minor}/${factor}` : '';
  return `${head}${fraction} ${currency} Only`.replace(/\s+/g, ' ').trim();
}

/**
 * How a currency is written in the spelled-out line. The reference invoice
 * says "SR", not "SAR" — these are the cheque-style short forms, and any
 * currency we don't have one for falls back to its ISO code.
 */
const CURRENCY_WORDS: Record<string, string> = {
  SAR: 'SR',
  AED: 'AED',
  KWD: 'KD',
  EGP: 'EGP',
  QAR: 'QR',
  BHD: 'BD',
  OMR: 'OMR',
  JOD: 'JD',
  USD: 'USD',
};

export function currencyWord(code: string): string {
  return CURRENCY_WORDS[code] ?? code;
}

/* ── Arabic ────────────────────────────────────────────────────────────────
 *
 * FlowFresh's A4 sheet spells the total in Arabic rather than English —
 * "عشرة ريال و واحد هللة فقط لا غير" under a total of 10.01. The unit words
 * are invariable there (ريال, not ريالات), which is how the ERP's own tafqit
 * writes them and therefore how their customers read it; only the number
 * itself is inflected.
 */

const AR_ONES = ['', 'واحد', 'اثنان', 'ثلاثة', 'أربعة', 'خمسة', 'ستة', 'سبعة', 'ثمانية', 'تسعة'];
const AR_TEENS = [
  'عشرة', 'أحد عشر', 'اثنا عشر', 'ثلاثة عشر', 'أربعة عشر', 'خمسة عشر',
  'ستة عشر', 'سبعة عشر', 'ثمانية عشر', 'تسعة عشر',
];
const AR_TENS = ['', '', 'عشرون', 'ثلاثون', 'أربعون', 'خمسون', 'ستون', 'سبعون', 'ثمانون', 'تسعون'];
const AR_HUNDREDS = [
  '', 'مائة', 'مائتان', 'ثلاثمائة', 'أربعمائة', 'خمسمائة',
  'ستمائة', 'سبعمائة', 'ثمانمائة', 'تسعمائة',
];

/** 1-999 in Arabic. Units lead their tens ("واحد وعشرون"), joined with و. */
function arUnderThousand(n: number): string {
  const parts: string[] = [];
  const hundreds = Math.floor(n / 100);
  const rest = n % 100;
  if (hundreds > 0) parts.push(AR_HUNDREDS[hundreds] as string);
  if (rest >= 10 && rest < 20) {
    parts.push(AR_TEENS[rest - 10] as string);
  } else if (rest > 0) {
    const tens = Math.floor(rest / 10);
    const ones = rest % 10;
    if (ones > 0 && tens >= 2) parts.push(`${AR_ONES[ones]} و${AR_TENS[tens]}`);
    else if (tens >= 2) parts.push(AR_TENS[tens] as string);
    else parts.push(AR_ONES[ones] as string);
  }
  return parts.join(' و');
}

/**
 * Name a scale group the way Arabic counts it: one and two have their own
 * words, three to ten take the plural, and eleven upwards the accusative
 * singular ("ثلاثة آلاف" but "أحد عشر ألفاً").
 */
function arScale(count: number, one: string, two: string, plural: string, many: string): string {
  if (count === 1) return one;
  if (count === 2) return two;
  if (count <= 10) return `${arUnderThousand(count)} ${plural}`;
  return `${arUnderThousand(count)} ${many}`;
}

/** A non-negative integer in Arabic words ('صفر' for 0). */
export function integerInWordsAr(value: number): string {
  const n = Math.floor(Math.abs(value));
  if (!Number.isFinite(n) || n === 0) return 'صفر';
  if (n >= 1_000_000_000) return String(n);

  const millions = Math.floor(n / 1_000_000);
  const thousands = Math.floor((n % 1_000_000) / 1000);
  const rest = n % 1000;

  const parts: string[] = [];
  if (millions > 0) parts.push(arScale(millions, 'مليون', 'مليونان', 'ملايين', 'مليوناً'));
  if (thousands > 0) parts.push(arScale(thousands, 'ألف', 'ألفان', 'آلاف', 'ألفاً'));
  if (rest > 0) parts.push(arUnderThousand(rest));
  return parts.join(' و');
}

/**
 * The spelled-out total for the FlowFresh sheet:
 * "عشرة ريال و واحد هللة فقط لا غير".
 *
 * The minor half is dropped entirely when the amount is whole, exactly as the
 * reference does — "خمسون ريال فقط لا غير", not "… و صفر هللة".
 */
export function amountInWordsAr(
  amount: number,
  precision = 2,
  major = 'ريال',
  minor = 'هللة',
): string {
  const safe = Number.isFinite(amount) ? amount : 0;
  const negative = safe < 0;
  const abs = Math.abs(safe);
  const whole = Math.floor(abs);
  const factor = 10 ** precision;
  let minorUnits = Math.round((abs - whole) * factor);
  let majorUnits = whole;
  if (minorUnits >= factor) {
    majorUnits += 1;
    minorUnits = 0;
  }

  const head = `${integerInWordsAr(majorUnits)} ${major}`;
  const tail = minorUnits > 0 ? ` و ${integerInWordsAr(minorUnits)} ${minor}` : '';
  return `${negative ? 'سالب ' : ''}${head}${tail} فقط لا غير`;
}
